import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";

interface ConfigurationSetting {
  id: number;
  alias: string;
  value: string;
  created_at: string;
  updated_at: string;
}

interface UpdateConfigurationSettingsPayload {
  settings: { alias: string; value: string }[];
}

interface ConfigurationSettingChange {
  id: number;
  alias: string;
  old_value: string | null;
  new_value: string;
  created_at: string;
  user_name: string;
}

interface KpsScoringConfig {
  relevance_max: number;
  search_volume_max: number;
  asin_density_max: number;
  hero_rank_max: number;
  search_trend_max: number;
  keyword_sales_max: number;
  competition_max: number;
  relevance_enabled: boolean;
  search_volume_enabled: boolean;
  asin_density_enabled: boolean;
  hero_rank_enabled: boolean;
  search_trend_enabled: boolean;
  keyword_sales_enabled: boolean;
  competition_enabled: boolean;
  // AUT-376: the hero-relevance penalty multipliers, named by the KPS breakdown tooltip when a
  // keyword's Relevance score was reduced. Nullable: an estate whose config rows predate them
  // falls back to the shipped defaults.
  competitor_brand_penalty_multiplier: number | null;
  no_overlap_penalty_multiplier: number | null;
}

const QUERY_KEY = ["configuration-settings"] as const;
const CHANGES_QUERY_KEY = ["configuration-setting-changes"] as const;
const KPS_SCORING_CONFIG_QUERY_KEY = ["kps-scoring-config"] as const;

export const useGetConfigurationSettings = () =>
  useQuery<ConfigurationSetting[]>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiService.get<ConfigurationSetting[]>("/configuration-settings"),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useUpdateConfigurationSettings = () =>
  useMutation<
    ConfigurationSetting[],
    Error,
    UpdateConfigurationSettingsPayload
  >({
    mutationFn: (payload) =>
      apiService.put<ConfigurationSetting[], UpdateConfigurationSettingsPayload>(
        "/configuration-settings",
        payload,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: CHANGES_QUERY_KEY });
    },
    onError: (error) => handleApiError(error),
  });

export const useRecalcPendingKps = () =>
  useMutation<{ status: string | null }, Error, void>({
    mutationFn: () =>
      apiService.post<{ status: string | null }, Record<string, never>>(
        "/configuration-settings/recalc-pending-kps",
        {},
      ),
    onError: (error) => handleApiError(error),
  });

export const useGetConfigurationSettingChanges = () =>
  useQuery<ConfigurationSettingChange[]>({
    queryKey: CHANGES_QUERY_KEY,
    queryFn: () =>
      apiService.get<ConfigurationSettingChange[]>(
        "/configuration-setting-changes",
      ),
    staleTime: DEFAULT_STALE_TIME,
  });

/**
 * Real, possibly org-customized KPS factor max-scores -- not admin-gated
 * (unlike useGetConfigurationSettings), so any role can read it. Backs the
 * Keyword Bank's KPS breakdown tooltip, which previously hardcoded these as
 * frontend constants (AUT-32 PR review).
 */
export const useGetKpsScoringConfig = () =>
  useQuery<KpsScoringConfig>({
    queryKey: KPS_SCORING_CONFIG_QUERY_KEY,
    queryFn: () => apiService.get<KpsScoringConfig>("/kps-scoring-config"),
    staleTime: DEFAULT_STALE_TIME,
  });

export type { ConfigurationSetting, ConfigurationSettingChange, KpsScoringConfig };
